import { Box, Button, CircularProgress, IconButton, Typography, useTheme } from '@mui/material';
import { AxiosError, AxiosResponse } from 'axios';
import { usePost } from 'hooks/use-post';
import { Check, CircleAlert } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import Drawer, { DrawerHandle } from 'components/drawer';
import FlexBox from 'components/flex-box';

import { ActiveOrder } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/types/order-assortment.type';

import { useAccountState } from 'contexts/account/account-context';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import { ErrorMessage } from 'types/shared/error-message.type';

import dayjs from 'dayjs';
import { useQueryClient } from '@tanstack/react-query';
import { deleteOrder as deleteOrderRequest } from 'views/order-feed/features/my-supply-plan/api/delete-order';
import EditOrderForm from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/components/orders/edit-order/edit-order-form';
import { useOrdersState } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/context/orders-context';
import { useGoogleTagManager } from 'views/order-feed/features/my-supply-plan/hooks/use-google-tag-manager';
import { DeleteOrderInput } from 'views/order-feed/features/my-supply-plan/types/delete-order-input.type';
import { OrderType, SupplyPlanType } from 'views/order-feed/features/my-supply-plan/types/google-tag-manager/order-feed-events.type';

const PAGE_NAME_ID = 'orderFeed.ofdMySupplyPlan.orders';
type Props = {
  order: ActiveOrder;
};

const ModalTitle: React.FC<Props> = ({ order }) => {
  const theme = useTheme();

  return (
    <Box display={'flex'} justifyContent={'center'} alignItems={'center'} gap={3}>
      <Box>
        <Typography fontWeight={'bold'}>
          <FormattedMessage id={`${PAGE_NAME_ID}.modals.confirmedDialog.title`} defaultMessage={order.orderStatus} />
        </Typography>
        <Typography color={theme.palette.darkBlue.o75}>#{order.orderNumber}</Typography>
      </Box>
      <IconButton sx={{ backgroundColor: theme.palette.green.o50, cursor: 'unset' }} disableRipple disableTouchRipple size="small">
        <Check color={theme.palette.darkBlue.main} size={17} />
      </IconButton>
    </Box>
  );
};

type DeleteOrderBoxProps = {
  onCancelDelete: () => void;
  orderNumber: string;
};

const DeleteOrderBox: React.FC<DeleteOrderBoxProps> = ({ onCancelDelete, orderNumber }) => {
  const intl = useIntl();
  const {
    account: { company },
  } = useAccountState();

  const { isLoading } = useOrdersState();

  const snackbarDispatch = useSnackbarDispatch();
  const queryClient = useQueryClient();

  const handleDeleteOrder = () => {
    removeOrder.mutate({
      orderNumber,
      deleteOrderInput: {
        customerErpId: String(company!.customerErpId),
      },
    });
  };

  const removeOrder = usePost<AxiosResponse, { orderNumber: string; deleteOrderInput: DeleteOrderInput }, AxiosError<ErrorMessage[]>>(
    (request) => deleteOrderRequest(request.orderNumber, request.deleteOrderInput),
    () => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });
      [['getTimeline'], ['getOfdSettings'], ['getOrderAssortment'], ['getCustomerAssortment'], ['getRecommendationAssortment']].forEach(
        (queryKey) => {
          queryClient.invalidateQueries({ queryKey });
        }
      );
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error, errorMsg: error.response?.data },
      });
    }
  );

  //#region RENDERS

  return (
    <Box display={'flex'} flexDirection={'column'} gap={4}>
      <Typography fontWeight={500} textAlign={'center'}>
        <FormattedMessage id={`${PAGE_NAME_ID}.modals.confirmedDialog.deleteOrder.deleteOrderMessage`} />
      </Typography>
      <Box display={'flex'} justifyContent={'space-between'}>
        <Button
          onClick={handleDeleteOrder}
          disabled={isLoading || removeOrder.isPending}
          startIcon={removeOrder.isPending ? <CircularProgress color="inherit" size={18} /> : null}>
          <FormattedMessage id={`${PAGE_NAME_ID}.modals.confirmedDialog.deleteButton`} />
        </Button>
        <Button onClick={onCancelDelete} variant="outlined" disabled={isLoading || removeOrder.isPending}>
          <FormattedMessage id={`general.cancel`} />
        </Button>
      </Box>
    </Box>
  );
};

const EditOrderDialog: React.FC<Props> = ({ order }) => {
  const intl = useIntl();
  const theme = useTheme();
  const { createOrderFeedEvent } = useGoogleTagManager();

  const drawerRef = useRef<DrawerHandle>(null);

  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);

  const { isLoading, isError, isSuccess, orderSettingsQuery } = useOrdersState();

  useEffect(() => {
    if (!isLoading && (isError || isSuccess)) {
      drawerRef.current?.close();
    }
  }, [isLoading]);

  return (
    <Drawer
      ref={drawerRef}
      onClose={() => {
        setIsConfirmingDelete(false);
      }}
      title={<ModalTitle order={order} />}
      button={
        <Button
          size="small"
          variant="outlined"
          disabled={dayjs().isAfter(order.lastPossibleEditDate, 'day') || orderSettingsQuery.data?.isUnderMaintenance}
          onClick={() => {
            createOrderFeedEvent('edit', {
              orderType: OrderType.CONFIRMED,
              orderNumber: order.orderNumber,
              supplyPlanType: SupplyPlanType.OFD2,
            });
          }}>
          <FormattedMessage id={`general.edit`} values={{ type: '' }} />
        </Button>
      }>
      <Box display={'flex'} flexDirection={'column'} width={600}>
        {!isConfirmingDelete ? (
          <Box>
            <FlexBox>
              <EditOrderForm showOrderDetails order={order}>
                <Box borderRadius={2} padding={5} sx={{ background: theme.palette.orange.light }} display={'flex'} gap={2}>
                  <Box alignItems={'center'} display={'flex'}>
                    <CircleAlert size={20} />
                  </Box>
                  <Box>
                    <Typography fontWeight={'bold'}>
                      <FormattedMessage
                        id={`${PAGE_NAME_ID}.modals.confirmedDialog.message`}
                        values={{
                          underlined: (
                            <u>
                              <FormattedMessage id={`${PAGE_NAME_ID}.modals.confirmedDialog.underlinedMessage`} />
                            </u>
                          ),
                        }}
                      />
                    </Typography>
                  </Box>
                </Box>
                <Box display={'flex'} justifyContent={'space-between'}>
                  <Button type="button" onClick={() => setIsConfirmingDelete(true)} variant="outlined">
                    <FormattedMessage id={`${PAGE_NAME_ID}.modals.confirmedDialog.deleteButton`} />
                  </Button>
                  <Button
                    startIcon={isLoading ? <CircularProgress color="inherit" size={18} /> : null}
                    type="submit"
                    disabled={isLoading}>
                    <FormattedMessage id={`${PAGE_NAME_ID}.modals.confirmedDialog.updateButton`} />
                  </Button>
                </Box>
              </EditOrderForm>
            </FlexBox>
          </Box>
        ) : (
          <DeleteOrderBox orderNumber={order.orderNumber} onCancelDelete={() => setIsConfirmingDelete(false)} />
        )}
      </Box>
    </Drawer>
  );
};

export default EditOrderDialog;
